import React, { useState, useEffect } from "react";

const UnitConverter = () => {
  const [inputValue, setInputValue] = useState(100);
  const [outputValue, setOutputValue] = useState(100);
  const [category, setCategory] = useState("length");
  const [fromUnit, setFromUnit] = useState("meter");
  const [toUnit, setToUnit] = useState("meter");
  const [financialYear, setFinancialYear] = useState("2023-24");
  const [result, setResult] = useState({
    inputValue: "0.00",
    outputValue: "0.00",
    fromUnit: "",
    toUnit: "",
    conversionRate: "0.00",
    formula: "",
    category: "",
    usageTip: "",
  });

  // Unit Conversion Function
  const convertUnits = () => {
    let input = parseFloat(inputValue);

    if (isNaN(input) || input < 0) return;

    let output = 0;
    let rate = 0;
    let formula = "";
    let categoryName = "";
    let usageTip = "";

    // Length conversions
    const lengthUnits = {
      meter: 1,
      kilometer: 0.001,
      centimeter: 100,
      millimeter: 1000,
      mile: 0.000621371,
      yard: 1.09361,
      foot: 3.28084,
      inch: 39.3701
    };

    // Weight conversions
    const weightUnits = {
      kilogram: 1,
      gram: 1000,
      milligram: 1000000,
      pound: 2.20462,
      ounce: 35.274,
      ton: 0.001
    };

    // Temperature conversions (special case)
    const convertTemperature = (value, from, to) => {
      if (from === to) return value;
      
      let celsius = value;
      if (from === "fahrenheit") celsius = (value - 32) * 5/9;
      if (from === "kelvin") celsius = value - 273.15;
      
      if (to === "celsius") return celsius;
      if (to === "fahrenheit") return (celsius * 9/5) + 32;
      if (to === "kelvin") return celsius + 273.15;
      
      return value;
    };

    // Volume conversions
    const volumeUnits = {
      liter: 1,
      milliliter: 1000,
      gallon: 0.264172,
      quart: 1.05669,
      pint: 2.11338,
      cup: 4.22675,
      fluid_ounce: 33.814
    };

    // Area conversions
    const areaUnits = {
      square_meter: 1,
      square_kilometer: 0.000001,
      square_mile: 0.000000386102,
      square_yard: 1.19599,
      square_foot: 10.7639,
      acre: 0.000247105,
      hectare: 0.0001
    };

    // Time conversions
    const timeUnits = {
      second: 1,
      minute: 0.0166667,
      hour: 0.000277778,
      day: 0.0000115741,
      week: 0.00000165344,
      month: 0.000000380517,
      year: 0.0000000316887
    };

    if (category === "length") {
      categoryName = "Length Conversion";
      output = (input * lengthUnits[fromUnit]) / lengthUnits[toUnit];
      rate = lengthUnits[toUnit] / lengthUnits[fromUnit];
      formula = `1 ${fromUnit} = ${rate.toFixed(6)} ${toUnit}`;
      usageTip = "Length conversions are commonly used in construction, travel distance, and scientific measurements.";
    } else if (category === "weight") {
      categoryName = "Weight/Mass Conversion";
      output = (input * weightUnits[fromUnit]) / weightUnits[toUnit];
      rate = weightUnits[toUnit] / weightUnits[fromUnit];
      formula = `1 ${fromUnit} = ${rate.toFixed(6)} ${toUnit}`;
      usageTip = "Weight conversions are essential for cooking, shipping, fitness tracking, and scientific experiments.";
    } else if (category === "temperature") {
      categoryName = "Temperature Conversion";
      output = convertTemperature(input, fromUnit, toUnit);
      formula = `Converting from ${fromUnit} to ${toUnit}`;
      usageTip = "Temperature conversions help with weather forecasts, cooking recipes, and scientific research.";
    } else if (category === "volume") {
      categoryName = "Volume Conversion";
      output = (input * volumeUnits[fromUnit]) / volumeUnits[toUnit];
      rate = volumeUnits[toUnit] / volumeUnits[fromUnit];
      formula = `1 ${fromUnit} = ${rate.toFixed(6)} ${toUnit}`;
      usageTip = "Volume conversions are useful for cooking, fuel measurement, and liquid capacity calculations.";
    } else if (category === "area") {
      categoryName = "Area Conversion";
      output = (input * areaUnits[fromUnit]) / areaUnits[toUnit];
      rate = areaUnits[toUnit] / areaUnits[fromUnit];
      formula = `1 ${fromUnit} = ${rate.toFixed(6)} ${toUnit}`;
      usageTip = "Area conversions help with real estate, land measurement, and construction planning.";
    } else if (category === "time") {
      categoryName = "Time Conversion";
      output = (input * timeUnits[fromUnit]) / timeUnits[toUnit];
      rate = timeUnits[toUnit] / timeUnits[fromUnit];
      formula = `1 ${fromUnit} = ${rate.toFixed(6)} ${toUnit}`;
      usageTip = "Time conversions are essential for project planning, travel itineraries, and scheduling.";
    }

    setResult({
      inputValue: input.toFixed(2),
      outputValue: output.toFixed(6),
      fromUnit: fromUnit,
      toUnit: toUnit,
      conversionRate: rate.toFixed(6),
      formula: formula,
      category: categoryName,
      usageTip: usageTip,
    });

    setOutputValue(output.toFixed(6));
  };

  useEffect(() => {
    convertUnits();
  }, []);

  const getCategoryUnits = (cat) => {
    switch(cat) {
      case "length": return ["meter", "kilometer", "centimeter", "millimeter", "mile", "yard", "foot", "inch"];
      case "weight": return ["kilogram", "gram", "milligram", "pound", "ounce", "ton"];
      case "temperature": return ["celsius", "fahrenheit", "kelvin"];
      case "volume": return ["liter", "milliliter", "gallon", "quart", "pint", "cup", "fluid_ounce"];
      case "area": return ["square_meter", "square_kilometer", "square_mile", "square_yard", "square_foot", "acre", "hectare"];
      case "time": return ["second", "minute", "hour", "day", "week", "month", "year"];
      default: return ["meter"];
    }
  };

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    const units = getCategoryUnits(newCategory);
    setFromUnit(units[0]);
    setToUnit(units[0]);
  };

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <h1>Universal Unit Converter</h1>
          <p>
            Convert between different units of measurement including length, weight, temperature, volume, area, and time
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Conversion Settings
              </h2>

              {/* Input Value */}
              <div className="input-group">
                <label>Input Value</label>
                <div className="input-wrapper">
                  <span className="input-prefix">#</span>
                  <input
                    type="number"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className="calc-input"
                    placeholder="100"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 10, 100, 1000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setInputValue(amount)}
                      className="quick-select-btn primary"
                    >
                      {amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Selection */}
              <div className="input-group">
                <label>Category</label>
                <div className="quick-select-group">
                  {[
                    { value: "length", label: "Length" },
                    { value: "weight", label: "Weight" },
                    { value: "temperature", label: "Temperature" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => handleCategoryChange(type.value)}
                      className={`quick-select-btn ${category === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "volume", label: "Volume" },
                    { value: "area", label: "Area" },
                    { value: "time", label: "Time" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => handleCategoryChange(type.value)}
                      className={`quick-select-btn ${category === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* From Unit */}
              <div className="input-group">
                <label>From Unit</label>
                <div className="input-wrapper">
                  <span className="input-prefix">→</span>
                  <select
                    value={fromUnit}
                    onChange={(e) => setFromUnit(e.target.value)}
                    className="calc-input"
                  >
                    {getCategoryUnits(category).map((unit) => (
                      <option key={unit} value={unit}>
                        {unit.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="quick-select-group">
                  {getCategoryUnits(category).slice(0, 4).map((unit) => (
                    <button
                      key={unit}
                      onClick={() => setFromUnit(unit)}
                      className="quick-select-btn success"
                    >
                      {unit.replace(/_/g, ' ').substring(0, 6)}
                    </button>
                  ))}
                </div>
              </div>

              {/* To Unit */}
              <div className="input-group">
                <label>To Unit</label>
                <div className="input-wrapper">
                  <span className="input-prefix">←</span>
                  <select
                    value={toUnit}
                    onChange={(e) => setToUnit(e.target.value)}
                    className="calc-input"
                  >
                    {getCategoryUnits(category).map((unit) => (
                      <option key={unit} value={unit}>
                        {unit.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="quick-select-group">
                  {getCategoryUnits(category).slice(0, 4).map((unit) => (
                    <button
                      key={unit}
                      onClick={() => setToUnit(unit)}
                      className="quick-select-btn success"
                    >
                      {unit.replace(/_/g, ' ').substring(0, 6)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Swap Units Button */}
              <div className="input-group">
                <label>Quick Actions</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Swap Units ⇄" },
                    { value: false, label: "Reset Values" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => {
                        if (type.value) {
                          const temp = fromUnit;
                          setFromUnit(toUnit);
                          setToUnit(temp);
                        } else {
                          setInputValue(100);
                          setOutputValue(100);
                        }
                      }}
                      className={`quick-select-btn ${type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Financial Year */}
              <div className="input-group">
                <label>Precision</label>
                <div className="quick-select-group">
                  {[
                    { value: "2023-24", label: "2 Decimals" },
                    { value: "2024-25", label: "6 Decimals" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setFinancialYear(type.value)}
                      className={`quick-select-btn ${financialYear === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={convertUnits} className="calculate-btn">
                Convert Units
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Conversion Result
              </h2>

              <div className="result-card">
                {/* Output Value - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Converted Value</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {Number(result.outputValue).toLocaleString("en-US")}
                  </div>
                  <div className="result-subtext">
                    {result.toUnit.replace(/_/g, ' ')} ({result.category})
                  </div>
                </div>

                {/* Input Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Original Value</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {Number(result.inputValue).toLocaleString("en-US")}
                  </div>
                  <div className="result-subtext">
                    {result.fromUnit.replace(/_/g, ' ')}
                  </div>
                </div>

                {/* Conversion Rate */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Conversion Rate</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.conversionRate}
                  </div>
                  <div className="result-subtext">
                    Multiplication factor
                  </div>
                </div>

                {/* Formula */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Conversion Formula</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value" style={{fontSize: '1rem'}}>
                    {result.formula}
                  </div>
                  <div className="result-subtext">
                    Base conversion rate
                  </div>
                </div>

                {/* Category Info */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Category</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value" style={{fontSize: '1.2rem'}}>
                    {result.category}
                  </div>
                  <div className="result-subtext">
                    Measurement type
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Conversion Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">From: {result.fromUnit.replace(/_/g, ' ')}</span>
                      <span className="breakdown-percentage">
                        {Number(result.inputValue).toLocaleString("en-US")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `50%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">To: {result.toUnit.replace(/_/g, ' ')}</span>
                      <span className="breakdown-percentage">
                        {Number(result.outputValue).toLocaleString("en-US")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `50%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Conversion Comparison */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Quick Reference</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">1 {result.fromUnit.replace(/_/g, ' ')}</span>
                      <span className="breakdown-percentage">
                        = {result.conversionRate} {result.toUnit.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `100%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">1 {result.toUnit.replace(/_/g, ' ')}</span>
                      <span className="breakdown-percentage">
                        = {(1 / parseFloat(result.conversionRate)).toFixed(6)} {result.fromUnit.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `100%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Usage Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Usage Tip</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.usageTip} Use the swap button to quickly reverse the conversion direction.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Unit Conversions</h3>
              <p>
                Unit conversion is the process of converting measurements from one unit to another within the same category. 
                This converter supports multiple categories including length (meters, feet, miles), weight (kilograms, pounds), 
                temperature (Celsius, Fahrenheit, Kelvin), volume (liters, gallons), area (square meters, acres), and time 
                (seconds, hours, days). Accurate unit conversion is essential in science, engineering, cooking, travel, and 
                everyday life. The metric system (SI units) is used internationally, while imperial units are common in the 
                United States. Understanding conversion factors helps ensure precision in measurements. For temperature, note 
                that the conversion formulas are different from other linear conversions due to different zero points and scales. 
                Always verify your conversions for critical applications like medication dosages, construction measurements, 
                or scientific experiments.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Common Conversion Formulas:</h4>
            <div className="formula-display">
              Output Value = Input Value × Conversion Rate
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Length</div>
                <div className="formula-param-value">1 meter = 3.28084 feet = 39.3701 inches</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Weight</div>
                <div className="formula-param-value">1 kilogram = 2.20462 pounds = 35.274 ounces</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Temperature</div>
                <div className="formula-param-value">°F = (°C × 9/5) + 32, K = °C + 273.15</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Volume</div>
                <div className="formula-param-value">1 liter = 0.264172 gallons = 33.814 fluid ounces</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is the difference between metric and imperial units?</h5>
              <p>
                Metric (SI) units are based on powers of 10 and used internationally. Imperial units are traditional measurements used primarily in the United States. Metric is generally easier for calculations.
              </p>
            </div>

            <div className="faq-item">
              <h5>How accurate are these conversions?</h5>
              <p>
                The conversions use standard international conversion factors and are accurate to 6 decimal places. For most practical purposes, this precision is more than sufficient.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why is temperature conversion different?</h5>
              <p>
                Temperature scales have different zero points and intervals. Celsius and Fahrenheit use different formulas because their scales are offset, while Kelvin is an absolute scale starting at absolute zero.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I convert between different categories?</h5>
              <p>
                No, you can only convert within the same category (e.g., length to length). Different categories measure different physical quantities and cannot be directly converted.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are some common conversion mistakes?</h5>
              <p>
                Common mistakes include confusing square units with linear units, mixing up Celsius and Fahrenheit formulas, and using incorrect conversion factors. Always double-check your category and units.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I remember conversion factors?</h5>
              <p>
                For frequent conversions, memorize key factors like 1 inch = 2.54 cm, 1 mile ≈ 1.6 km, 1 kg ≈ 2.2 lbs. Use this converter or create flashcards for others you need regularly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitConverter;